import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";

import TruncatedWithTooltip from "../../../../../components/TruncatedWithTooltip";
import Avatar from "../../../../../components/Avatar";
import dayjs from "dayjs";
import "dayjs/locale/ar";

import { UITable, UIRowActionsMenu } from "../../../../../components/ui";
import RejectReasonModal from "../../../../../components/modals/RejectReasonModal";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";
import { clx } from "../../../../../utils/Helpers";

const EMPTY_VALUE = "-";

const getCompanyName = (row, lang) =>
  lang === "ar"
    ? row.name_ar || row.name_en || EMPTY_VALUE
    : row.name_en || row.name_ar || EMPTY_VALUE;

const getCompanySubName = (row, lang) =>
  lang === "ar" ? row.name_en || EMPTY_VALUE : row.name_ar || EMPTY_VALUE;

const getContactName = (row, lang) =>
  row.company_owner?.name ||
  row.company_owner?.[`full_name_${lang}`] ||
  row.company_owner?.full_name_en ||
  row.contact_person_name ||
  row.contact_name ||
  EMPTY_VALUE;

const getContactEmail = (row) =>
  row.company_owner?.email ||
  row.contact_person_email ||
  row.contact_email ||
  row.email ||
  EMPTY_VALUE;

const getCity = (row, lang) =>
  row.city?.[`name_${lang}`] ||
  row.city?.name_en ||
  row.city_name ||
  EMPTY_VALUE;

const CompanyCell = ({ row, lang }) => {
  const title = getCompanyName(row, lang);
  const subtitle = getCompanySubName(row, lang);

  return (
    <div className="registration-req-table__company">
      <Avatar
        imgSrc={row.image_url}
        name={row.name_en || row.name_ar || title}
        size={40}
        containerClass="flex-shrink-0"
      />
      <div className="registration-req-table__stack">
        <TruncatedWithTooltip title={title}>
          <span className="MuiDataGrid-cellContent">{title}</span>
        </TruncatedWithTooltip>
        <TruncatedWithTooltip title={subtitle}>
          <span className="MuiDataGrid-cellContent registration-req-table__muted">
            {subtitle}
          </span>
        </TruncatedWithTooltip>
      </div>
    </div>
  );
};

CompanyCell.propTypes = {
  row: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
};

const StackCell = ({ title, subtitle, subtitleClassName = "registration-req-table__muted" }) => (
  <div className="registration-req-table__stack">
    <TruncatedWithTooltip title={title}>
      <span className="MuiDataGrid-cellContent">{title}</span>
    </TruncatedWithTooltip>
    {subtitle && subtitle !== EMPTY_VALUE && (
      <TruncatedWithTooltip title={subtitle}>
        <span
          className={clx(
            "MuiDataGrid-cellContent",
            subtitleClassName,
          )}
        >
          {subtitle}
        </span>
      </TruncatedWithTooltip>
    )}
  </div>
);

StackCell.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitleClassName: PropTypes.string,
};

StackCell.defaultProps = {
  subtitle: null,
  subtitleClassName: "registration-req-table__muted",
};

const TextCell = ({ value, className }) => (
  <span className={clx("MuiDataGrid-cellContent", className)}>{value}</span>
);

TextCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  className: PropTypes.string,
};

TextCell.defaultProps = {
  className: undefined,
};

const RegistrationTable = ({
  rows,
  status,
  messagesBundle,
  shared,
  onApprove,
  onReject,
}) => {
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const [rejectTarget, setRejectTarget] = useState(null);
  const isPending = status === "pending";

  const companyDetailsPath = (companyId) =>
    generatePath(ROUTE_PATHS.registrationRequestCompanyView, { id: companyId });

  const goToCompanyDetails = (companyId) => {
    history.push(companyDetailsPath(companyId));
  };

  const formatCreated = (iso) =>
    dayjs(iso)
      .locale(lang === "ar" ? "ar" : "en")
      .format("DD MMM YYYY");

  const rejectName = rejectTarget?.name || EMPTY_VALUE;
  const rejectDescParts = messagesBundle.rejectModalDescription.split("{name}");

  const getRegistrationRowActionItems = (row) => [
    {
      key: "approve",
      label: shared.approve,
      success: true,
      onClick: () => void onApprove(row.id),
    },
    {
      key: "reject",
      label: messagesBundle.reject,
      danger: true,
      onClick: () =>
        setRejectTarget({
          id: row.id,
          name: getCompanyName(row, lang),
        }),
    },
  ];

  const columns = [
    {
      field: "company",
      headerName: messagesBundle.company || "Company",
      flex: 1.35,
      minWidth: 230,
      sortable: false,
      renderCell: (params) => <CompanyCell row={params.row} lang={lang} />,
    },
    {
      field: "commercial_record",
      headerName: messagesBundle.commercialRecord,
      flex: 0.9,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <TextCell value={params.value || EMPTY_VALUE} />
      ),
    },
    {
      field: "contactPerson",
      headerName: messagesBundle.contactPerson,
      flex: 1.1,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => (
        <StackCell
          title={getContactName(params.row, lang)}
          subtitle={getContactEmail(params.row)}
          subtitleClassName="registration-req-table__email"
        />
      ),
    },
    {
      field: "city",
      headerName: messagesBundle.city,
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => <TextCell value={getCity(params.row, lang)} />,
    },
    {
      field: "created_at",
      headerName: messagesBundle.requestedDate || messagesBundle.creationDate,
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <TextCell
          value={
            params.row.created_at
              ? formatCreated(params.row.created_at)
              : EMPTY_VALUE
          }
        />
      ),
    },
    {
      field: "actions",
      headerName: shared.actions,
      flex: 0.55,
      minWidth: 84,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        isPending ? (
          <UIRowActionsMenu
            ariaLabel={shared.actions}
            items={getRegistrationRowActionItems(params.row)}
          />
        ) : (
          <TextCell value={EMPTY_VALUE} />
        ),
    },
  ];

  return (
    <>
      <div className="registration-req-table-wrap">
        <UITable
          rows={rows}
          columns={columns}
          contentSized
          hideFooter
          className="registration-req-table"
          getRowClassName={() => "registration-req-table__row"}
          onRowClick={(params) => goToCompanyDetails(params.row.id)}
        />
      </div>
      <RejectReasonModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={async (reason) => {
          if (rejectTarget) {
            await onReject(rejectTarget.id, reason);
          }
        }}
        title={messagesBundle.rejectModalTitle}
        modalClassName="registration-req-reject-modal"
        description={
          <>
            {rejectDescParts[0]}
            <strong>{rejectName}</strong>
            {rejectDescParts[1] ?? ""}
          </>
        }
        reasonLabel={messagesBundle.rejectionReasonLabel}
        reasonPlaceholder={messagesBundle.rejectionReasonPlaceholder}
        confirmLabel={messagesBundle.confirmRejection}
        cancelLabel={shared.cancel}
        minReasonLength={3}
        requiredErrorMessage={messagesBundle.required}
        minLengthErrorMessage={messagesBundle.rejectionReasonMinLength}
        reasonInputId="reject-registration-reason-table"
      />
    </>
  );
};

RegistrationTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  status: PropTypes.oneOf(["pending", "rejected"]).isRequired,
  messagesBundle: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default RegistrationTable;
